import React, { useRef, useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ocrService from '../../../services/ocrService';

const CameraViewfinder = ({ onVinDetected, isScanning, onToggleCamera }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    if (isScanning) {
      startCamera();
    } else {
      stopCamera();
    }

    return () => {
      stopCamera();
    };
  }, [isScanning]);

  // Auto-start camera when component mounts if scanning is enabled
  useEffect(() => {
    if (isScanning && hasPermission === null) {
      startCamera();
    }
  }, []);

  const startCamera = async () => {
    try {
      setError(null);
      
      // Debug: Log available APIs
      console.log('Available camera APIs:', {
        mediaDevices: !!navigator.mediaDevices,
        getUserMedia: !!navigator.getUserMedia,
        webkitGetUserMedia: !!navigator.webkitGetUserMedia,
        mozGetUserMedia: !!navigator.mozGetUserMedia,
        userAgent: navigator.userAgent
      });
      
      // Check if we're on HTTPS (required for camera access)
      if (location.protocol !== 'https:' && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
        throw new Error('Kamera-Zugriff erfordert HTTPS. Bitte öffnen Sie die Seite über eine sichere Verbindung.');
      }
      
      // Better camera API detection for mobile compatibility
      let getUserMedia = null;
      
      // For mobile Chrome, we need to ensure mediaDevices is available
      if (!navigator.mediaDevices) {
        // Create mediaDevices if it doesn't exist (for older browsers)
        navigator.mediaDevices = {};
      }
      
      // Try different ways to get getUserMedia
      if (navigator.mediaDevices.getUserMedia) {
        getUserMedia = navigator.mediaDevices.getUserMedia.bind(navigator.mediaDevices);
        console.log('Using navigator.mediaDevices.getUserMedia');
      } else if (navigator.getUserMedia) {
        getUserMedia = navigator.getUserMedia.bind(navigator);
        console.log('Using navigator.getUserMedia');
      } else if (navigator.webkitGetUserMedia) {
        getUserMedia = navigator.webkitGetUserMedia.bind(navigator);
        console.log('Using navigator.webkitGetUserMedia');
      } else if (navigator.mozGetUserMedia) {
        getUserMedia = navigator.mozGetUserMedia.bind(navigator);
        console.log('Using navigator.mozGetUserMedia');
      }
      
      if (!getUserMedia) {
        console.error('No getUserMedia API found, trying to create polyfill...');
        
        // Create a comprehensive polyfill for mobile browsers
        const getUserMediaPolyfill = function(constraints) {
          const getUserMedia = navigator.webkitGetUserMedia || 
                              navigator.mozGetUserMedia || 
                              navigator.msGetUserMedia;
          
          if (!getUserMedia) {
            return Promise.reject(new Error('getUserMedia is not implemented in this browser'));
          }
          
          return new Promise(function(resolve, reject) {
            getUserMedia.call(navigator, constraints, resolve, reject);
          });
        };
        
        // Assign to mediaDevices if it exists
        if (navigator.mediaDevices) {
          navigator.mediaDevices.getUserMedia = getUserMediaPolyfill;
          getUserMedia = getUserMediaPolyfill;
          console.log('Created polyfill for navigator.mediaDevices.getUserMedia');
        } else {
          throw new Error('Kamera-API wird nicht unterstützt. Bitte verwenden Sie einen modernen Browser.');
        }
      }

      // Try to get camera access with better mobile compatibility
      let mediaStream;
      
      try {
                 // First try with detailed constraints
         mediaStream = await getUserMedia({
           video: {
             facingMode: { ideal: 'environment' }, // Prefer back camera on mobile
             width: { ideal: 1920, max: 3840 },
             height: { ideal: 1080, max: 2160 },
             aspectRatio: { ideal: 16/9 }
           },
           audio: false
         });
      } catch (constraintError) {
        console.log('Detailed constraints failed, trying simplified constraints:', constraintError);
        
        try {
          // Fallback to simpler constraints for older browsers
          mediaStream = await getUserMedia({
            video: {
              facingMode: 'environment' // Prefer back camera on mobile
            },
            audio: false
          });
        } catch (simpleConstraintError) {
          console.log('Simplified constraints failed, trying basic video:', simpleConstraintError);
          
          try {
            // Final fallback - just request video without any constraints
            mediaStream = await getUserMedia({
              video: true,
              audio: false
            });
          } catch (basicError) {
            console.log('Basic video failed, trying direct getUserMedia:', basicError);
            
            // Last resort - try direct getUserMedia if available
            if (navigator.getUserMedia) {
              mediaStream = await new Promise((resolve, reject) => {
                navigator.getUserMedia({ video: true, audio: false }, resolve, reject);
              });
            } else {
              throw basicError;
            }
          }
        }
      }
      
      setStream(mediaStream);
      setHasPermission(true);
      
      if (videoRef?.current) {
        videoRef.current.srcObject = mediaStream;
        // Ensure video loads properly
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(console.error);
        };
      }
    } catch (err) {
      console.error('Camera access error:', err);
      setHasPermission(false);
      
      if (err.name === 'NotAllowedError') {
        setError('Kamera-Zugriff verweigert. Bitte erlauben Sie den Kamera-Zugriff in Ihren Browser-Einstellungen.');
      } else if (err.name === 'NotFoundError') {
        setError('Keine Kamera gefunden. Bitte stellen Sie sicher, dass Ihr Gerät über eine Kamera verfügt.');
      } else if (err.name === 'NotSupportedError') {
        setError('Kamera wird nicht unterstützt. Bitte verwenden Sie einen modernen Browser.');
      } else if (err.name === 'NotReadableError') {
        setError('Kamera ist bereits in Verwendung. Bitte schließen Sie andere Apps, die die Kamera verwenden.');
      } else if (err.name === 'OverconstrainedError') {
        setError('Kamera-Anforderungen können nicht erfüllt werden. Versuchen Sie es mit anderen Einstellungen.');
      } else if (err.message === 'Kamera-API wird nicht unterstützt') {
        setError('Ihr Browser unterstützt die Kamera-API nicht. Bitte verwenden Sie Chrome, Safari oder Firefox.');
      } else if (err.message.includes('HTTPS')) {
        setError(err.message);
      } else {
        setError(`Kamera-Fehler: ${err.message || 'Unbekannter Fehler beim Zugriff auf die Kamera'}`);
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream?.getTracks()?.forEach(track => track?.stop());
      setStream(null);
    }
  };

  const switchCamera = async () => {
    if (stream) {
      stopCamera();
      // Small delay to ensure camera is fully stopped
      await new Promise(resolve => setTimeout(resolve, 500));
      startCamera();
    }
  };

  const captureFrame = () => {
    if (!videoRef?.current || !canvasRef?.current) return null;

    const video = videoRef?.current;
    const canvas = canvasRef?.current;
    const ctx = canvas?.getContext('2d');

    // Set canvas to camera window size (320x80)
    const targetWidth = 320;
    const targetHeight = 80;
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    // Calculate the crop area from the center of the video
    const videoWidth = video?.videoWidth;
    const videoHeight = video?.videoHeight;
    
    console.log('📹 Video dimensions:', { videoWidth, videoHeight });
    console.log('🎯 Target dimensions:', { targetWidth, targetHeight });
    
    // Calculate aspect ratio of video vs target
    const videoAspect = videoWidth / videoHeight;
    const targetAspect = targetWidth / targetHeight;
    
    console.log('📐 Aspect ratios:', { videoAspect, targetAspect });
    
    let sourceX, sourceY, sourceWidth, sourceHeight;
    
    if (videoAspect > targetAspect) {
      // Video is wider than target, crop from sides
      sourceHeight = videoHeight;
      sourceWidth = videoHeight * targetAspect;
      sourceX = (videoWidth - sourceWidth) / 2;
      sourceY = 0;
    } else {
      // Video is taller than target, crop from top/bottom
      sourceWidth = videoWidth;
      sourceHeight = videoWidth / targetAspect;
      sourceX = 0;
      sourceY = (videoHeight - sourceHeight) / 2;
    }

    console.log('✂️ Crop area:', { sourceX, sourceY, sourceWidth, sourceHeight });

    // Draw the cropped area to the canvas
    ctx?.drawImage(
      video,
      sourceX, sourceY, sourceWidth, sourceHeight,  // Source rectangle
      0, 0, targetWidth, targetHeight               // Destination rectangle
    );

    const imageData = canvas?.toDataURL('image/jpeg', 0.8);
    console.log('🖼️ Final image data length:', imageData?.length);
    
    return imageData;
  };

  const handleCapture = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    const imageData = captureFrame();
    
    if (imageData) {
      try {
        // Debug: Log image data info
        console.log('📸 Captured image data length:', imageData.length);
        console.log('📸 Image data preview:', imageData.substring(0, 100) + '...');
        
        // Extract VIN from image using Google Vision API
        console.log('🔍 Extracting VIN from captured image...');
        const extractedVin = await ocrService.extractVinFromImage(imageData);
        
        console.log('✅ VIN successfully extracted:', extractedVin);
        onVinDetected(extractedVin, imageData);
      } catch (error) {
        console.error('❌ OCR Error:', error.message);
        
        // Fallback to manual input if OCR fails
        const manualVin = prompt(`OCR-Fehler: ${error.message}\n\nBitte geben Sie den VIN-Code manuell ein:`);
        
        if (manualVin && manualVin.trim()) {
          const cleanVin = manualVin.trim().toUpperCase();
          if (ocrService.validateVin(cleanVin)) {
            onVinDetected(cleanVin, imageData);
          } else {
            alert('Ungültiges VIN-Format. VIN muss 17 Zeichen lang sein und darf keine I, O oder Q enthalten.');
          }
        }
      } finally {
        setIsProcessing(false);
      }
    } else {
      setIsProcessing(false);
    }
  };

  if (hasPermission === false) {
    return (
      <div className="relative w-80 h-20 bg-muted rounded-lg flex flex-col items-center justify-center p-2 mx-auto">
        <Icon name="Camera" size={24} className="text-muted-foreground mb-2" />
        <h3 className="text-sm font-semibold text-foreground mb-1">Kamera-Zugriff erforderlich</h3>
        <p className="text-xs text-muted-foreground text-center mb-2">
          {error || 'Bitte erlauben Sie den Kamera-Zugriff, um VIN-Codes zu scannen.'}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={startCamera}>
            <Icon name="Camera" size={12} className="mr-1" />
            Aktivieren
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            <Icon name="RefreshCw" size={12} className="mr-1" />
            Neu laden
          </Button>
        </div>
      </div>
    );
  }

  if (hasPermission === null && isScanning) {
    return (
      <div className="relative w-80 h-20 bg-muted rounded-lg flex flex-col items-center justify-center p-2 mx-auto">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mb-2"></div>
        <h3 className="text-sm font-semibold text-foreground mb-1">Kamera wird gestartet...</h3>
        <p className="text-xs text-muted-foreground text-center">
          Bitte warten Sie, während die Kamera initialisiert wird.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-4">
      {/* Camera Window */}
      <div className="relative w-80 h-20 bg-black rounded-lg overflow-hidden">
        {/* Video Element */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          controls={false}
          className="w-full h-full object-cover"
        />
        
        {/* Canvas for frame capture */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Overlay Guide Frame */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative">
            {/* Guide Frame */}
            <div className="w-full h-full border-2 border-accent rounded-lg bg-transparent">
              {/* Corner indicators */}
              <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-accent"></div>
              <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-accent"></div>
              <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-accent"></div>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-accent"></div>
            </div>
          </div>
        </div>

        {/* Processing Overlay */}
        {isProcessing && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-card rounded-lg p-2 flex flex-col items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mb-1"></div>
              <p className="text-xs font-medium">VIN wird erkannt...</p>
            </div>
          </div>
        )}
      </div>

      {/* Control Buttons */}
      <div className="flex items-center space-x-4">
        <Button
          variant="secondary"
          size="icon"
          onClick={onToggleCamera}
          className="w-10 h-10 rounded-full bg-card hover:bg-muted"
        >
          <Icon name={isScanning ? "CameraOff" : "Camera"} size={16} />
        </Button>
        
        <Button
          variant="default"
          size="icon"
          onClick={handleCapture}
          disabled={!isScanning || isProcessing}
          className="w-12 h-12 rounded-full"
        >
          <Icon name="Circle" size={24} />
        </Button>
        
        <Button
          variant="secondary"
          size="icon"
          onClick={switchCamera}
          className="w-10 h-10 rounded-full bg-card hover:bg-muted"
        >
          <Icon name="RotateCcw" size={16} />
        </Button>
      </div>
    </div>
  );
};

export default CameraViewfinder;